import { Component, OnInit, OnDestroy, Input } from "@angular/core";
import { RemindersService } from "../../../services/reminders.service";
import { Reminder, ReminderDeliveryMethod, ReminderType, ReminderDeliveryMethodOptions, ReminderTypeOptions } from "../../../models/reminders.model";
import { Option } from "../../../../common/models/flow.dto";
import { Router, ActivatedRoute } from "@angular/router";
import { GroupStub } from "../../../models/group.model";
import { GroupService } from "../../../../admin/services/group.service";
import { SubHeaderService } from "../../../../core/services/sub-header.service";

import { Observable, Subscription, pipe, forkJoin } from "rxjs";
import { DocumentManagementService } from "../../../../common/services/document-management.service";
import { DomainValue } from "../../../../common/models/document-tag.model";
import { genId } from "../../../../core/services/genId";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { FormGroup } from "@angular/forms";
import { finalize } from "rxjs/operators";
import { ReportSelectorService } from "../../../../form-controls/services/report-selector.service";
import { ReportTemplate } from "../../../../models/comodel/mergefieldsmodel.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { LoaderService } from "../../../../common/services/loader.service";

@Component({
    selector: "reminder-edit",
    templateUrl: "./reminder-edit.component.html",
    styleUrls: ["./reminder-edit.component.scss"],
    standalone: false
})
export class ReminderEditComponent implements OnInit, OnDestroy {
  public reminder: Reminder;
  public groups: GroupStub[];
  public inGroups: GroupStub[] = [];
  public nonGroups: GroupStub[] = [];
  public documentTypes: DomainValue[];
  public allDeliveryMethods = new Array<Option>();
  public allReminderTypes = new Array<Option>();
  public templateTypes: ReportTemplate[];
  public subHeader: SubHeader = new SubHeader({ title: "Manage Reminder", buttons: [{ name: "Save", method: () => this.onSubmit() }, { name: "Cancel", method: () => this._returnToList() }] });
  public selectedAddGroupsIdList = new Array<string>();
  public editReminderForm: FormGroup;
  public selectedRemoveGroupsIdList = new Array<string>();
  public reminderFetched: boolean = false;
  public reminderTemplatesFetched: boolean = false;
  public selectedTemplateDocumentType: string = "N/A";
  dataSubscriptions: Subscription = new Subscription();


  invalidReminderDialog: Dialog = new Dialog("Please provide valid values for the reminder's required fields", [new DialogButton("OK", false, () => { return; })]);
  saveSuccessDialog: Dialog = new Dialog("The reminder has successfully been saved", [new DialogButton("OK", false, () => { this.onSaveSuccessConfirm() })]);

  constructor(
    public _route: ActivatedRoute,
    public _svc: RemindersService,
    public _router: Router,
    public _groupSvc: GroupService,
    public _docSvc: DocumentManagementService,
    public readonly _reportTemplateSvc: ReportSelectorService,
    public subHeaderService: SubHeaderService,
    public loader: LoaderService,
    private _dialogService: DynamicDialogService
  ) {}

  ngOnDestroy() {
      if (this.dataSubscriptions){
          this.dataSubscriptions.unsubscribe();
    }
  }

  ngOnInit() {
    this.loader.toggleLoader(true);
    this.subHeaderService.updateSubHeader(this.subHeader);
    //delivery methods
    this.allDeliveryMethods = ReminderDeliveryMethodOptions.All;
    //reminder types
    this.allReminderTypes = ReminderTypeOptions.All;
    //groups
    var getGroupsSubscription$ = (this._groupSvc.get({ stubs: true, internal: true }) as Observable<GroupStub[]>)
        .pipe(
            finalize(() => {
                this.dataSubscriptions.add(getGroupsSubscription$);
            })
        ).subscribe(
            data => { this.groups = data; this.setInGroups(); },
            error => { console.log(error); alert("ERROR Fetching Groups"); }
        );

    //load new or existing
    var getRouteParamsSubscription$ = this._route.params
    .pipe(
        finalize(() => {
            this.dataSubscriptions.add(getRouteParamsSubscription$);
        })
    ).subscribe(
        routeParams => {
            if (routeParams["id"] === "new") {
                this.reminder = {
                    id: genId(),
                    name: "New Reminder",
                    active: false,
                    deliveryMethods: ReminderDeliveryMethod.CertifiedMail,
                    documentTypeId: "",
                    getEverythingWithinTheTimeframe: true,
                    internalGroupsToNotifyIds: [] = [],
                    timeframe: 90,
                    timeframeIsInBusinessDays: false,
                    reportTemplateId: "",
                    type: ReminderType.BondingCertificateOfDepositMaturation
                };
                this.setInGroups();
                if (this.reminderTemplatesFetched)
                    this.templateChanged(this.reminder.reportTemplateId);
                this.reminderFetched = true;
                if (this.allNeededDataFetched())
                    this.loader.toggleLoader(false);
            } else {
                let reminderId = routeParams["id"];
                var getRouteIdSubscription$ = this._svc.get(reminderId)
                    .pipe(
                        finalize(() => {
                            this.dataSubscriptions.add(getRouteIdSubscription$);
                        })
                    ).subscribe(
                        data => {
                            this.reminder = data;
                            this.setInGroups();
                            this.reminderFetched = true;
                            this.templateChanged(this.reminder.reportTemplateId);
                            if (this.allNeededDataFetched())
                                this.loader.toggleLoader(false);
                        },
                        error => {
                            console.log(error);
                            alert("ERROR Fetching Reminder");
                            this.reminderFetched = true;
                            if (this.allNeededDataFetched())
                                this.loader.toggleLoader(false);
                        }
                );
            }
        },
        error => { console.log(error); alert("ERROR Fetching Reminder based on route parameters"); }
        ); 

        //template types
        var templateTypesSubscription$ = this._reportTemplateSvc.getTemplates('reminder')
        .pipe(
            finalize(() => {
                this.dataSubscriptions.add(templateTypesSubscription$);
            })
        ).subscribe(
            templates =>
            {
                this.templateTypes = templates;
                this.reminderTemplatesFetched = true;
                if (this.reminderFetched)
                    this.templateChanged(this.reminder.reportTemplateId);
                if (this.allNeededDataFetched())
                    this.loader.toggleLoader(false);
            },
            error => {
                console.log(error);
                alert("ERROR Fetching Text Templates");
                if (this.allNeededDataFetched())
                    this.loader.toggleLoader(false);
            }
        );

  }

  public reminderHasDeliveryMethod(deliveryMethod: string): boolean {
    const deliveryMethodNumber: number = parseInt(deliveryMethod);
    let reminder = this.reminder;
    let value = (reminder.deliveryMethods & deliveryMethodNumber) !== 0;
    return value;
  }

  public isRegularOrCertified(): boolean {
    if (this.reminderHasDeliveryMethod(ReminderDeliveryMethod.RegularMail.toString()) || this.reminderHasDeliveryMethod(ReminderDeliveryMethod.CertifiedMail.toString())) {
      return true;
    } else {
      return false;
    }
  }

  public isNotification(): boolean {
    return this.reminderHasDeliveryMethod(ReminderDeliveryMethod.Notification.toString());
  }

  public toggleGetEverythingWithinTheTimeframe(): void {
    this.reminder.getEverythingWithinTheTimeframe = !this.reminder.getEverythingWithinTheTimeframe;
  }
    

  /**
   * Save reminder updates
   * */
  onSubmit() {
      if (this.reminderIsValid()) {
          this.loader.toggleLoader(true);
            var reminderSaveSubscription$ = this._svc.post(this.reminder)
                .pipe(
                    finalize(() => {
                        this.dataSubscriptions.add(reminderSaveSubscription$);
                    })
                ).subscribe(
                    next => {
                        this.saveSuccessDialog.toggleDialog(true);
                        this._dialogService.updateDialog(this.saveSuccessDialog);
                        this.loader.toggleLoader(false);
                    },
                    error => { alert("ERROR Saving Reminder"); console.log(error); this.loader.toggleLoader(false); }
                );
        }
        else {
            this.invalidReminderDialog.toggleDialog(true);
            this._dialogService.updateDialog(this.invalidReminderDialog);
        }

    }

    onSaveSuccessConfirm() {
        this._returnToList();
    }

  /**
   * Cancel edits
   * */
  _returnToList() {
    this._router.navigate(["admin", "reminders"]);
  }


  /**
   * Groups checkbox change
   * @param groupId
   * @param event
   */
  groupsChange(groupId: string, event: any) {
    if(event.target.checked) {
      //add to array
      this.reminder.internalGroupsToNotifyIds.push(groupId);
    } else {
      //remove from array
      this.reminder.internalGroupsToNotifyIds = this.reminder.internalGroupsToNotifyIds.filter(id => id !== groupId);
    }
  }

  /**
   * For checking group checkboxes
   * @param groupId
   */
  isInGroups(groupId: string):boolean {
    for (let id of this.reminder.internalGroupsToNotifyIds) {
      if (id == groupId) { return true; }
    }
    return false;
  }

  /**
   * For mulit-select - set both sides of groups/non-groups
   * */

    //TODO: make the call to this only happen on a forkjoin of groups and reminder both being fetched, so that we can have the call in only one place.
  setInGroups():void {
      if (this.groups && this.reminder && this.reminder.internalGroupsToNotifyIds && this.groups.length > 0 ) {
      for (let i = 0; i < this.groups.length; i++) {
        let group = this.groups[i];
        if (this.isInGroups(group.id)) {
          this.inGroups.push(group);
        } else {
          this.nonGroups.push(group);
        }
      }
    }
  }

  /**
   * Post select mulitple, update reminder.internalGroupsNotifyIds
   * @param added
   */
  syncInternalGroupsToNotify(added: boolean): void {
    if (added) {
      for (let add of this.selectedAddGroupsIdList) {
        this.reminder.internalGroupsToNotifyIds.push(add);
      }
    } else {
      for (let add of this.selectedRemoveGroupsIdList) {
        this.reminder.internalGroupsToNotifyIds = this.reminder.internalGroupsToNotifyIds.filter(id => id !== add);
      }
    }
  }

  /**
   * Clicked ">>"
   * */
  addGroup(): void {
    for (let add of this.selectedAddGroupsIdList) {
      //add to inGroups
      this.inGroups.push(this.groups.find(group => group.id === add));
      //remove from nonGroups
      this.nonGroups = this.nonGroups.filter(group => group.id !== add);
    }
    this.syncInternalGroupsToNotify(true);
  }

  /**
   * Clicked "<<"
   * */
  removeGroup(): void {
    for (let remove of this.selectedRemoveGroupsIdList) {
      //remove from inGroups
      this.inGroups = this.inGroups.filter(group => group.id !== remove);
      //add to nonGroups
      this.nonGroups.push(this.groups.find(group => group.id === remove));
    }
    this.syncInternalGroupsToNotify(false);
  }

  /**
   * Delivery Methods checkbox change
   * @param methodId
   * @param checked
   */
  deliveryMethodsUpdated(methodId: string, checked: boolean) {
    const methodNumber: number = parseInt(methodId);

    //If they uncheck an option that exists on reminder, remove it
    if (!checked && this.reminderHasDeliveryMethod(methodId)) {
      this.reminder.deliveryMethods -= methodNumber;
    }
    //If they check an option that doesn't exist on reminder, add it
    else if (checked && !this.reminderHasDeliveryMethod(methodId)) {
      this.reminder.deliveryMethods += methodNumber;
    }
  }
    //The reminder type has changed, filter the available templates accordingly
    typeChanged(code: string): void {

        if (this.filteredTemplateTypes.length > 0 && this.isntNullOrEmpty(this.filteredTemplateTypes[0].id))
            this.reminder.reportTemplateId = this.filteredTemplateTypes[0].id;

        this.templateChanged(this.reminder.reportTemplateId)
    }

    //The template type has changed, set the template's documentType for display
    templateChanged(templateId: string): void {
        //try to find a the matching template and set it's documenttype
        if (templateId == "" && this.filteredTemplateTypes.length > 0)
            templateId = this.filteredTemplateTypes[0].id

        let matchingDocumentType = this.filteredTemplateTypes.filter(a => a.id == templateId);
        if (matchingDocumentType.length > 0 && this.isntNullOrEmpty(matchingDocumentType[0].id)) {
            this.selectedTemplateDocumentType = matchingDocumentType[0].onBaseDocumentTypeName;
        }
        else
            this.selectedTemplateDocumentType = "N/A";
    }

    isntNullOrEmpty(stringToTest: string) {
        return (stringToTest !== undefined && stringToTest !== null && stringToTest !== "");
    }

    private reminderIsValid(): boolean {
        return this.reminder.deliveryMethods != 0 && this.isntNullOrEmpty(this.reminder.reportTemplateId) && this.reminder.timeframe > 0;
    }


    get filteredTemplateTypes() {
        let matchingTemplates: ReportTemplate[] = [];

        if (this.templateTypes) {
            matchingTemplates = this.templateTypes.filter(a => Number(a.categoryCode) == this.reminder.type);
        }

        if (matchingTemplates.length == 0)
            matchingTemplates = [{ id: null, name: "No templates for this type" } as ReportTemplate];

        return matchingTemplates;
    }

    allNeededDataFetched(): boolean {
        let everythingFetched = this.reminderTemplatesFetched && this.reminderFetched
        return everythingFetched;
    }
}
